import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
    Box, Container, Typography, Paper, Grid, Skeleton, Chip, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
    People, SportsEsports, TrendingUp, Sensors, Verified, EmojiEvents, TravelExplore,
} from '@mui/icons-material';
import {
    ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip as RechartsTooltip, PieChart, Pie, Cell,
} from 'recharts';
import NavBar from '../../Components/NavBar/navbar';
import { useAuth } from '../../Context/AuthContext';
import { API_ENDPOINTS } from '../../utils/config';
import { THEME } from '../../theme';
import { buildGameNameMap, gameDisplayName, shortUsername } from '../Arcade/Dashboard/displayHelpers';
import { gamesData } from '../../Components/Arcade/gamesConfig';

const POLL_LIVE_MS = 10000;
const PIE_COLORS = ['#2cbb5d', '#3b82f6', '#fbbf24', '#ef4743', '#a78bfa', '#22d3ee', '#f472b6', '#84cc16'];

const StatCard = ({ icon, label, value, sub, loading, color }) => (
    <Paper sx={{ p: 2.5, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
                width: 44, height: 44, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: `${color}22`, color, flexShrink: 0,
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {label}
                </Typography>
                {loading ? (
                    <Skeleton variant="text" width={70} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                ) : (
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {value}
                    </Typography>
                )}
                {sub && !loading && (
                    <Typography variant="caption" sx={{ color: THEME.secondary }}>{sub}</Typography>
                )}
            </Box>
        </Box>
    </Paper>
);

const SectionPaper = ({ title, subtitle, children }) => (
    <Paper sx={{ bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, overflow: 'hidden', height: '100%' }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: `1px solid ${THEME.border}` }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 800 }}>{title}</Typography>
            {subtitle && (
                <Typography variant="body2" sx={{ color: THEME.secondary }}>{subtitle}</Typography>
            )}
        </Box>
        <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
);

async function fetchLiveSessionCount() {
    try {
        const res = await fetch(`${API_ENDPOINTS.SDK_SESSIONS}?mine=0`);
        if (!res.ok) return 0;
        const data = await res.json();
        return (data.sessions || []).filter((s) => !s.status || s.status === 'active').length;
    } catch (e) {
        return 0;
    }
}

async function fetchRecentActivity() {
    try {
        const res = await fetch(API_ENDPOINTS.RECENT_RUNS({ limit: 8 }));
        if (!res.ok) return [];
        const data = await res.json();
        return data.runs || data.submissions || (Array.isArray(data) ? data : []);
    } catch (e) {
        return [];
    }
}

/**
 * Admin-only site analytics dashboard: total/active users, engagement,
 * per-game popularity, growth trends, and a live tracker. Server-side gated
 * (GET /api/admin/analytics returns 401/403 for non-admins) — this component
 * additionally redirects non-admins away so the page never mounts for them.
 * Visual language matches src/Pages/Arcade/Dashboard/* (THEME, card style).
 */
const AdminDashboard = () => {
    const { user, token, login, loading: authLoading } = useAuth();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [liveCount, setLiveCount] = useState(0);
    const [recent, setRecent] = useState([]);
    const [games, setGames] = useState([]);
    // Cached `user` in localStorage may predate the privilege_level field
    // (or a since-revoked grant) — refetch /me for a fresh, server-truth check.
    const [meChecked, setMeChecked] = useState(false);
    const [meUser, setMeUser] = useState(user);

    useEffect(() => {
        if (!token) { setMeChecked(true); return; }
        let mounted = true;
        fetch(API_ENDPOINTS.ME, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!mounted) return;
                if (data && data.user) {
                    setMeUser(data.user);
                    if (data.user.privilege_level !== user?.privilege_level) {
                        login(data.user, token);
                    }
                }
            })
            .catch(() => {})
            .finally(() => { if (mounted) setMeChecked(true); });
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const isAdmin = meUser && meUser.privilege_level === 'admin';

    const loadAnalytics = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(API_ENDPOINTS.ADMIN_ANALYTICS, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `status ${res.status}`);
            }
            const data = await res.json();
            setAnalytics(data);
        } catch (e) {
            setError(e.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (isAdmin) loadAnalytics();
    }, [isAdmin, loadAnalytics]);

    useEffect(() => {
        fetch(API_ENDPOINTS.GAMES_LIST)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => d && setGames(Array.isArray(d) ? d : (d.games || [])))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        let mounted = true;
        let timer;
        async function tick() {
            const [count, activity] = await Promise.all([fetchLiveSessionCount(), fetchRecentActivity()]);
            if (!mounted) return;
            setLiveCount(count);
            setRecent(activity);
            timer = setTimeout(tick, POLL_LIVE_MS);
        }
        tick();
        return () => { mounted = false; if (timer) clearTimeout(timer); };
    }, [isAdmin]);

    const gameNameMap = useMemo(() => buildGameNameMap([...games, ...gamesData]), [games]);

    const perGameChartData = useMemo(() => {
        if (!analytics) return [];
        return analytics.per_game_popularity.slice(0, 8).map((row) => ({
            name: gameDisplayName(row.game_id, gameNameMap),
            runs: row.runs,
        }));
    }, [analytics, gameNameMap]);

    const sessionsChartData = useMemo(() => {
        if (!analytics) return [];
        return analytics.sessions_over_time.map((row) => ({
            date: row.date.slice(5),
            runs: row.runs,
            users: row.distinct_users,
        }));
    }, [analytics]);

    const signupsChartData = useMemo(() => {
        if (!analytics) return [];
        return analytics.signups_over_time.map((row) => ({
            date: row.date.slice(5),
            signups: row.signups,
        }));
    }, [analytics]);

    const hitsChartData = useMemo(() => {
        if (!analytics?.hits_over_time) return [];
        return analytics.hits_over_time.map((row) => ({
            date: row.date.slice(5),
            hits: row.hits,
        }));
    }, [analytics]);

    // Auth still resolving (localStorage read, or the /me freshness check) — avoid a flash redirect.
    if (authLoading || !meChecked) return null;

    // Client-side gate: hide/redirect non-admins. Server-side gate lives in
    // general_db_services.py `require_admin` on GET /api/admin/analytics.
    if (!meUser || meUser.privilege_level !== 'admin') {
        return <Navigate to="/" replace />;
    }

    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="xl">
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h2" sx={{ color: 'white', fontWeight: 800, mb: 1, letterSpacing: '-1px' }}>
                            Admin <span style={{ color: THEME.primary }}>Analytics</span>
                        </Typography>
                        <Typography variant="body1" sx={{ color: THEME.secondary }}>
                            Site-wide metrics — users, traffic, engagement, and growth.
                        </Typography>
                    </Box>

                    {error && (
                        <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(239, 71, 67, 0.08)', border: `1px solid ${THEME.accent}`, borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ color: THEME.accent }}>
                                Failed to load analytics ({error}).
                            </Typography>
                        </Paper>
                    )}

                    {/* --- Live tracker (reuses SDK sessions infra, same source as LiveSessionsPanel) --- */}
                    <Paper sx={{ mb: 4, p: 2.5, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', bgcolor: 'rgba(44, 187, 93, 0.15)', color: THEME.primary,
                        }}>
                            <Sensors />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ color: THEME.secondary }}>Live right now</Typography>
                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>
                                {liveCount} active session{liveCount === 1 ? '' : 's'}
                            </Typography>
                        </Box>
                        <Chip size="small" label="Auto-refreshing" sx={{ bgcolor: 'rgba(44, 187, 93, 0.15)', color: THEME.primary, fontWeight: 700 }} />
                    </Paper>

                    {/* --- Top-line stat cards --- */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard icon={<People />} label="Total Users" value={analytics?.users.total ?? 0} sub={`${analytics?.users.registered ?? 0} registered · ${analytics?.users.guests ?? 0} guest`} loading={loading} color={THEME.primary} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard icon={<TravelExplore />} label="Total Website Hits" value={analytics?.hits?.total ?? 0} sub={`Today ${analytics?.hits?.today ?? 0} · 7d ${analytics?.hits?.last_7d ?? 0} · 30d ${analytics?.hits?.last_30d ?? 0}`} loading={loading} color="#22d3ee" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard icon={<TrendingUp />} label="Daily Active" value={analytics?.users.dau ?? 0} sub={`WAU ${analytics?.users.wau ?? 0} · MAU ${analytics?.users.mau ?? 0}`} loading={loading} color="#3b82f6" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard icon={<SportsEsports />} label="Total Games Played" value={analytics?.engagement.total_games_played ?? 0} sub={`${analytics?.engagement.verified_runs ?? 0} verified runs`} loading={loading} color="#fbbf24" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard icon={<Verified />} label="7-Day Retention" value={analytics?.engagement.retention_rate_7d_pct != null ? `${analytics.engagement.retention_rate_7d_pct}%` : '—'} sub={`${analytics?.engagement.retention_cohort_retained ?? 0}/${analytics?.engagement.retention_cohort_eligible ?? 0} returned`} loading={loading} color="#a78bfa" />
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <SectionPaper title="Runs & Active Players Over Time" subtitle="Last 30 days">
                                {loading ? (
                                    <Skeleton variant="rectangular" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                                ) : sessionsChartData.length === 0 ? (
                                    <Typography sx={{ color: THEME.secondary, textAlign: 'center', py: 6 }}>No runs recorded in the last 30 days.</Typography>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <LineChart data={sessionsChartData}>
                                            <CartesianGrid stroke={THEME.grid} strokeDasharray="3 3" />
                                            <XAxis dataKey="date" stroke={THEME.secondary} fontSize={12} />
                                            <YAxis stroke={THEME.secondary} fontSize={12} allowDecimals={false} />
                                            <RechartsTooltip contentStyle={{ background: THEME.cardBg, border: `1px solid ${THEME.border}` }} />
                                            <Line type="monotone" dataKey="runs" name="Runs" stroke={THEME.primary} strokeWidth={2} dot={false} />
                                            <Line type="monotone" dataKey="users" name="Distinct users" stroke="#3b82f6" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </SectionPaper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SectionPaper title="Website Hits Over Time" subtitle="Last 30 days · all visitors">
                                {loading ? (
                                    <Skeleton variant="rectangular" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                                ) : hitsChartData.length === 0 ? (
                                    <Typography sx={{ color: THEME.secondary, textAlign: 'center', py: 6 }}>No website hits recorded yet.</Typography>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <LineChart data={hitsChartData}>
                                            <CartesianGrid stroke={THEME.grid} strokeDasharray="3 3" />
                                            <XAxis dataKey="date" stroke={THEME.secondary} fontSize={12} />
                                            <YAxis stroke={THEME.secondary} fontSize={12} allowDecimals={false} />
                                            <RechartsTooltip contentStyle={{ background: THEME.cardBg, border: `1px solid ${THEME.border}` }} />
                                            <Line type="monotone" dataKey="hits" name="Hits" stroke="#22d3ee" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </SectionPaper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SectionPaper title="New Signups" subtitle="Last 30 days">
                                {loading ? (
                                    <Skeleton variant="rectangular" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                                ) : signupsChartData.length === 0 ? (
                                    <Typography sx={{ color: THEME.secondary, textAlign: 'center', py: 6 }}>No new signups in the last 30 days.</Typography>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={signupsChartData}>
                                            <CartesianGrid stroke={THEME.grid} strokeDasharray="3 3" />
                                            <XAxis dataKey="date" stroke={THEME.secondary} fontSize={12} />
                                            <YAxis stroke={THEME.secondary} fontSize={12} allowDecimals={false} />
                                            <RechartsTooltip contentStyle={{ background: THEME.cardBg, border: `1px solid ${THEME.border}` }} />
                                            <Bar dataKey="signups" fill={THEME.primary} radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </SectionPaper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <SectionPaper title="Per-Game Popularity" subtitle="All-time run counts, top 8 games">
                                {loading ? (
                                    <Skeleton variant="rectangular" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                                ) : perGameChartData.length === 0 ? (
                                    <Typography sx={{ color: THEME.secondary, textAlign: 'center', py: 6 }}>No submissions yet.</Typography>
                                ) : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie data={perGameChartData} dataKey="runs" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name }) => name}>
                                                {perGameChartData.map((entry, idx) => (
                                                    <Cell key={entry.name} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip contentStyle={{ background: THEME.cardBg, border: `1px solid ${THEME.border}` }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </SectionPaper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SectionPaper title="Top Players" subtitle="Highest verified score, all-time">
                                {loading ? (
                                    <Skeleton variant="rectangular" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                                ) : (analytics?.top_players || []).length === 0 ? (
                                    <Typography sx={{ color: THEME.secondary, textAlign: 'center', py: 6 }}>No verified runs yet.</Typography>
                                ) : (
                                    <TableContainer sx={{ maxHeight: 260 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderColor: THEME.border }}>#</TableCell>
                                                    <TableCell sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderColor: THEME.border }}>Player</TableCell>
                                                    <TableCell sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderColor: THEME.border }} align="right">Best Score</TableCell>
                                                    <TableCell sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderColor: THEME.border }} align="right">Runs</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {analytics.top_players.map((p, idx) => (
                                                    <TableRow key={p.user_id || idx}>
                                                        <TableCell sx={{ color: THEME.secondary, borderColor: THEME.border }}>{idx + 1}</TableCell>
                                                        <TableCell sx={{ color: 'white', borderColor: THEME.border }}>
                                                            <EmojiEvents fontSize="inherit" sx={{ color: idx === 0 ? '#fbbf24' : THEME.secondary, verticalAlign: 'middle', mr: 0.5 }} />
                                                            {shortUsername(p.username || p.user_id)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: THEME.primary, fontWeight: 700, borderColor: THEME.border }}>{p.best_score}</TableCell>
                                                        <TableCell align="right" sx={{ color: THEME.secondary, borderColor: THEME.border }}>{p.runs}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </SectionPaper>
                        </Grid>
                    </Grid>

                    <SectionPaper title="Recent Activity" subtitle="Latest completed runs across all games (live feed)">
                        {recent.length === 0 ? (
                            <Typography sx={{ color: THEME.secondary, textAlign: 'center', py: 3 }}>No recent activity.</Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: THEME.secondary, borderColor: THEME.border }}>Player</TableCell>
                                            <TableCell sx={{ color: THEME.secondary, borderColor: THEME.border }}>Game</TableCell>
                                            <TableCell sx={{ color: THEME.secondary, borderColor: THEME.border }} align="right">Score</TableCell>
                                            <TableCell sx={{ color: THEME.secondary, borderColor: THEME.border }}>When</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recent.map((r, idx) => (
                                            <TableRow key={r._id || r.run_id || idx}>
                                                <TableCell sx={{ color: 'white', borderColor: THEME.border }}>{shortUsername(r.username || r.user_id)}</TableCell>
                                                <TableCell sx={{ color: THEME.secondary, borderColor: THEME.border }}>{gameDisplayName(r.game_id, gameNameMap)}</TableCell>
                                                <TableCell align="right" sx={{ color: THEME.primary, fontWeight: 700, borderColor: THEME.border }}>{r.score ?? '—'}</TableCell>
                                                <TableCell sx={{ color: THEME.secondary, borderColor: THEME.border }}>
                                                    {r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </SectionPaper>
                </Container>
            </Box>
        </Box>
    );
};

export default AdminDashboard;
